import { Badge, Button } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { moeda } from '@/comuns/formato';
import type { EventoDeAuditoria } from '@/data/tipos';
import { quandoCompleto } from './apoioDoPainel';

const ROTULOS: Record<string, string> = {
    de: 'De',
    para: 'Para',
    motivo: 'Motivo',
    plano: 'Plano',
    situacao: 'Situação',
    apelido: 'Apelido',
    percentual: 'Percentual',
    meses: 'Meses',
    tipo: 'Tipo',
    descricao: 'Descrição',
    vencimento: 'Vencimento',
    competencia: 'Competência',
    chave: 'Flag',
    nome: 'Nome',
    prefixo: 'Prefixo',
    valorCentavos: 'Valor',
    saldoPerdidoCentavos: 'Saldo perdido',
};

function valorLegivel(chave: string, valor: unknown): string {
    if (valor === null || valor === undefined || valor === '') return '—';
    if (/centavos$/i.test(chave) && typeof valor === 'number') return moeda(valor);
    if (chave === 'percentual' && typeof valor === 'number') return `${valor}%`;
    if (Array.isArray(valor)) return valor.length ? valor.map(String).join(', ') : 'nenhuma';
    if (typeof valor === 'object') return JSON.stringify(valor);
    return String(valor);
}

/**
 * O detalhe de um evento do log.
 *
 * Mostra o que o banco devolve: a ação, onde, quem e quando — e o DETALHE só
 * nas ações da própria plataforma, que a função do operador escreveu sem dado de
 * paciente. Nas demais, o que mudou fica de fora de propósito.
 */
export function DetalheDaAuditoriaModal({ evento, onFechar }: { evento: EventoDeAuditoria | null; onFechar: () => void }) {
    const daPlataforma = evento?.acao.startsWith('plataforma.') ?? false;

    return (
        <Modal
            open={evento !== null} onClose={onFechar} width={540}
            title={evento?.acao ?? ''}
            description={evento ? quandoCompleto(evento.ocorridoEm) : ''}
            footer={<Button variant="secondary" onClick={onFechar}>Fechar</Button>}
        >
            {evento && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <span style={{ display: 'flex', gap: 8 }}>
                        <Badge tone={daPlataforma ? 'info' : 'neutral'}>{daPlataforma ? 'ação da plataforma' : 'ação na clínica'}</Badge>
                    </span>
                    <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '10px 16px', fontSize: 13 }}>
                        <dt style={{ color: 'var(--text-secondary)' }}>Clínica</dt><dd style={{ margin: 0, fontWeight: 500 }}>{evento.clinica}</dd>
                        <dt style={{ color: 'var(--text-secondary)' }}>Autor</dt><dd style={{ margin: 0, fontWeight: 500 }}>{evento.autorNome}</dd>
                        <dt style={{ color: 'var(--text-secondary)' }}>Tabela</dt><dd style={{ margin: 0 }}><code>{evento.tabela || '—'}</code></dd>
                        <dt style={{ color: 'var(--text-secondary)' }}>Registro</dt><dd style={{ margin: 0 }}><code style={{ wordBreak: 'break-all' }}>{evento.registroId ?? '—'}</code></dd>
                    </dl>

                    {evento.detalhe ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <strong style={{ fontSize: 11.5, letterSpacing: 'var(--tracking-wide)', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>
                                Detalhe da ação
                            </strong>
                            <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 16px', fontSize: 13 }}>
                                {Object.entries(evento.detalhe).map(([chave, valor]) => (
                                    <div key={chave} style={{ display: 'contents' }}>
                                        <dt style={{ color: 'var(--text-secondary)' }}>{ROTULOS[chave] ?? chave}</dt>
                                        <dd style={{ margin: 0 }}>{valorLegivel(chave, valor)}</dd>
                                    </div>
                                ))}
                            </dl>
                        </div>
                    ) : (
                        <Alert tone="neutral" dismissible={false} title="Sem detalhe">
                            {daPlataforma
                                ? 'Este evento foi registrado sem detalhe.'
                                : 'Só as ações da própria plataforma mostram o detalhe. Nas demais, o que mudou pode conter dado de paciente e fica de fora — ver o conteúdo exige impersonação, que também é registrada.'}
                        </Alert>
                    )}
                </div>
            )}
        </Modal>
    );
}
